import { useEffect, useState } from 'react';

/**
 * Custom hook to detect if a media query matches
 * @param query - The media query string to match against
 * @returns boolean - Whether the media query matches
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    // Check if window is available (for SSR compatibility)
    if (!globalThis.window) {
      return;
    }

    try {
      const media = globalThis.window.matchMedia(query);

      // Set initial value
      setMatches(media.matches);

      // Create listener function
      const listener = (event: MediaQueryListEvent) => {
        setMatches(event.matches);
      };

      // Add listener
      media.addEventListener('change', listener);

      // Cleanup
      // eslint-disable-next-line consistent-return
      return () => {
        media.removeEventListener('change', listener);
      };
    } catch {
      // If matchMedia fails, default to false
      setMatches(false);
    }
  }, [query]);

  return matches;
}

// Common breakpoint hooks for convenience
export const useIsMobile = (): boolean => useMediaQuery('(max-width: 639px)');
export const useIsTablet = (): boolean =>
  useMediaQuery('(min-width: 640px) and (max-width: 1023px)');
export const useIsMobileOrTablet = (): boolean =>
  useMediaQuery('(max-width: 1023px)');
export const useIsDesktop = (): boolean => useMediaQuery('(min-width: 1024px)');
export const useIsLargeDesktop = () => useMediaQuery('(min-width: 1280px)');

// Specific breakpoint hooks matching Tailwind CSS breakpoints
export const useIsSm = () => useMediaQuery('(min-width: 640px)');
export const useIsMd = () => useMediaQuery('(min-width: 768px)');
export const useIsLg = () => useMediaQuery('(min-width: 1024px)');
export const useIsXl = () => useMediaQuery('(min-width: 1280px)');
export const useIs2xl = () => useMediaQuery('(min-width: 1536px)');

// Orientation hooks
export const useIsPortrait = () => useMediaQuery('(orientation: portrait)');
export const useIsLandscape = () => useMediaQuery('(orientation: landscape)');

// Motion preference hooks
export const usePrefersReducedMotion = () =>
  useMediaQuery('(prefers-reduced-motion: reduce)');
export const usePrefersDarkMode = () =>
  useMediaQuery('(prefers-color-scheme: dark)');

// High DPI/Retina display detection
export const useIsHighDPI = () =>
  useMediaQuery(
    '(-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi)'
  );

// Common responsive patterns
export const useIsSmallScreen = () => useMediaQuery('(max-width: 767px)');
export const useIsMediumScreen = () =>
  useMediaQuery('(min-width: 768px) and (max-width: 1023px)');
export const useIsLargeScreen = () => useMediaQuery('(min-width: 1024px)');
